import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, filter, fromEvent, map, merge, Observable, Subject, tap } from 'rxjs';
import { IStringAnyMap } from '../types';
import { BOOX_APPLICATION_GUEST, BOOX_APPLICATION_HOST, BooxApplicationToken } from '../token';
import moment from 'moment';
import { addToFirstPlace, randomUuid } from '../helper';

export interface IMessageEvent {
  type : string;
  src : string;

  dest : string;
  _dest ?: string;

  ts : number;
  id : string;

  [key : string] : any;
}

export interface IBooxMessageEvent {
  type : string;
  src : string;

  dest : string;
  _dest? : string;

  ts : number;
  id : string;

  direction ?: 'in' | 'out';

  data : { [key : string] : any };

  [key : string] : any;
}

@Injectable({
  providedIn: 'root'
})
export class BooxMessageService {

  // public message stream
  // all incoming and outgoing messages of this client
  public message$ : Observable<IBooxMessageEvent>;
  // all outgoing messages of this client
  public output$ : Observable<IBooxMessageEvent>;
  // all incoming messages of this client
  public input$ : Observable<IBooxMessageEvent>;

  public log$ : BehaviorSubject<IBooxMessageEvent[]> = new BehaviorSubject<IBooxMessageEvent[]>([]);

  private application : BooxApplicationToken| null = null;
  private isHost : boolean = false;

  // global input message stream
  private windowMessage$ : Observable<MessageEvent> = fromEvent<MessageEvent>(window, 'message');
  // internal message stream
  private internalMessage$ : Subject<IMessageEvent> = new Subject<IMessageEvent>();

  // target window
  private target : {[key : string] : any} = {};

  constructor(
    @Inject(BOOX_APPLICATION_HOST) private application_host: BooxApplicationToken,
    @Inject(BOOX_APPLICATION_GUEST) private application_guest: BooxApplicationToken,
  ) {

    this.application = this.application_host || this.application_guest || null;
    this.isHost = !!this.application_host;

    const {
      call_sign : application_call_sign,
      name : application_name,
    } = this.application;

    // all none host application will target the window of the parent
    // an other binding is not possible
    // just host application can bind to other window
    if(!this.isHost){
      this.target['host'] = window.parent;
    }

    // all outgoing message will be broadcasted
    // to this stream as well
    this.output$ = this.internalMessage$.pipe(
      // transform the message to BooxMessageEvent
      map(this._toBooxMessage.bind(this)),
      map((message : IBooxMessageEvent) => ({
        ...message,
        direction : 'out'
      }))
    );
    // extract IMessageEvent from message Event
    this.input$ = this.windowMessage$.pipe(
      map((event: MessageEvent) => event.data as IMessageEvent),
      // make sure the message is valid
      filter((message : IMessageEvent) => !!message?.type ),
      // transform the message to BooxMessageEvent
      map(this._toBooxMessage.bind(this))
    );

    this.message$ = merge(
      this.input$,
      // all outgoing messages will be broadcasted to stream as well
      // the filter will make sure that the message is not broadcasted twice
      this.output$,
    ).pipe(
      filter((message : IBooxMessageEvent) => {
        const { type, dest = 'all' } = message;
        if (!type) return false;

        // check if the message is for this application
        return [
            `all.**`,
            `${application_name}.**`,
            this.isHost ? `host.**` : `app.**`
        ].some(pattern => this._checkPatern(dest, pattern));
      }),
      map((message : IBooxMessageEvent) => ({
        ...message,
        direction : 'in'
      } as IBooxMessageEvent)),
      map((message: IBooxMessageEvent) => {
        const { dest = 'all', _dest = '' } = message;
        const [first, ...rest] = dest.split('.');
        return {
          ...message,
          dest: rest.join('.'), // Remove the first part of `dest`
          _dest: [_dest, first].filter(Boolean).join('.') // Keep `_dest` clean
        };
      }),
      tap((message : IBooxMessageEvent) => {
        this.log$.next(addToFirstPlace<IBooxMessageEvent>(this.log$.value, message, 5));
      }),
    );
  }

  private _checkPatern( key : string, pattern : string ) : boolean {

    // Convert the pattern into a valid regex string
    const regexPattern = pattern
        .split('.')
        .map(part => {
            if (part === '**') return '.*'; // Allow an empty string
            if (part === '*') return '[^.]*'; // Matches anything except a dot
            return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // Escape regex characters
        })
        .join('\\.?'); // Use optional escaped dot

    // Create regex for full match and test the key
    return new RegExp(`^${regexPattern}$`).test(key);
  }

  private _toBooxMessage(message : IMessageEvent) : IBooxMessageEvent {
    const { id, type, ts, dest, _dest, src, direction, ...data } = message;
    const booxMessage : IBooxMessageEvent = { id, type, ts, dest, _dest, src, data };
    return booxMessage;
  };

  public bind(application_name : string, target : Window){

    if(!this.isHost){
      throw { message : 'Only host application can register target' }
    }

    this.target[application_name] = target;
  }

  /**
   * unbind application from message service
   * @param application_name app name to unbind
   */
  public unbind(application_name : string){
    delete this.target[application_name];
  }

  // Filter message by type
  public on$( type : string, destination ?: string) : Observable<IBooxMessageEvent> {
    return this.message$.pipe(
      // filter by internal destination when set
      filter((message: IBooxMessageEvent) => !destination || this._checkPatern(message.dest, destination)),
      // filter by type
      filter((message: IBooxMessageEvent) => this._checkPatern(message.type, type))
    );
  };

  // Send message to child
  public to(type: string, data: IStringAnyMap, dest: string = 'all', src ?: string) : void {

    if(!this.application) return;

    let id = randomUuid();
    src = [this.application.name, src].filter(Boolean).join('.');

    const message : IMessageEvent= {
      ...data,
      type,
      dest,
      src,
      ts : moment().valueOf(),
      id,
    };

    Object.entries(this.target).forEach(([key, target]) => {
      target.postMessage(message, '*');
    });
    this.internalMessage$.next(message);
  }
}
